import { useRef, useEffect, useMemo, useState } from "react";
import {
  FestivalCompetitionBannerAddress,
  FestivalCompetitionBannerText,
  FestivalCompetitionBannerWrapper,
  FestivalCompetitionImageBanner,
  RabbitFestivalCompetitionScrollingWrapper,
} from "./style";
import { useRabbitFestivalContext } from "../../../../hooks/festival-context";

const RabbitFestivalCompetitionBanner = () => {
  const { competition, events, setShowHeader } = useRabbitFestivalContext();
  const bannerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const sponsorRef = useRef<HTMLDivElement>(null);
  const sponsorScrollRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(10);
  const [sponsorDuration, setSponsorDuration] = useState(5);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // 스폰서 이미지 리스트를 동적으로 확장
  const extendedSponsors = useMemo(() => {
    if (!competition || !competition?.sponsorImages.length) return [];

    const sponsors = competition?.sponsorImages.map((item) => item.imageUrl);

    // 기본적으로 3배로 확장 (seamless scrolling을 위해)
    let multiplier = 3;

    // 이미지가 적을 경우 더 많이 복제
    if (sponsors.length < 5) {
      multiplier = 6;
    } else if (sponsors.length < 10) {
      multiplier = 4;
    }

    const extended = [];
    for (let i = 0; i < multiplier; i++) {
      extended.push(...sponsors);
    }

    return extended;
  }, [competition]);

  const address = useMemo(() => {
    return events.map((item) => item.competition?.location || "");
  }, [events]);

  // header 투명 여부
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
          setShowHeader(true);
        } else {
          setShowHeader(false);
        }
      },
      {
        threshold: 0.5,
      }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [bannerRef.current]);

  const getDate = () => {
    const start = new Date(competition?.startDate || "");
    const end = new Date(competition?.endDate || "");
    const startMonth = start.getMonth() + 1;
    const startDay = start.getDate();
    const endMonth = end.getMonth() + 1;
    const endDay = end.getDate();

    return `${startMonth}월 ${startDay}일부터 ${endMonth}월 ${endDay}일까지`;
  };

  // 주소 텍스트 애니메이션 속도 조절
  useEffect(() => {
    if (!textRef.current || competition?.sponsorImages.length === 0) return;
    const textWidth = textRef.current.scrollWidth;
    const baseSpeed = 30;
    setDuration(textWidth / baseSpeed);
  }, [competition, textRef.current]);

  // 스폰서 이미지 로딩 완료 체크 및 애니메이션 속도 설정
  useEffect(() => {
    if (!sponsorScrollRef.current || !extendedSponsors.length) return;

    const images = sponsorScrollRef.current.querySelectorAll("img"); // img 태그 조회
    let loadedCount = 0;

    const checkAllImagesLoaded = () => {
      loadedCount++;
      if (loadedCount === images.length) {
        setImagesLoaded(true);
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        // img 태그 로드 완료 여부
        checkAllImagesLoaded();
      } else {
        img.addEventListener("load", checkAllImagesLoaded);
        img.addEventListener("error", checkAllImagesLoaded);
      }
    });

    return () => {
      images.forEach((img) => {
        img.removeEventListener("load", checkAllImagesLoaded);
        img.removeEventListener("error", checkAllImagesLoaded);
      });
    };
  }, [extendedSponsors]);

  // 스폰서 애니메이션 duration 설정
  useEffect(() => {
    if (!imagesLoaded || !sponsorRef.current || !sponsorScrollRef.current)
      return;

    // 이미지 로딩 후 잠시 대기
    const timer = setTimeout(() => {
      const containerWidth = sponsorRef.current?.offsetWidth || 0;
      const scrollWidth = sponsorScrollRef.current?.scrollWidth || 0;

      if (scrollWidth > 0 && containerWidth > 0) {
        const scrollDistance = scrollWidth / 3;
        const baseSpeed = 22;
        const calculatedDuration = Math.max(scrollDistance / baseSpeed, 3);

        setSponsorDuration(calculatedDuration);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [imagesLoaded, extendedSponsors]);

  return (
    <FestivalCompetitionBannerWrapper ref={bannerRef}>
      <FestivalCompetitionImageBanner>
        <img src={competition?.bannerImage} alt="banner" />
        <div className="back" />
        <FestivalCompetitionBannerText>
          {address.length > 0 && (
            <FestivalCompetitionBannerAddress duration={duration}>
              <div className="rolling-track">
                <div className="rolling-text" ref={textRef}>
                  {[...address, ...address].map((text, i) => (
                    <div key={i}>{text}</div>
                  ))}
                </div>
              </div>
            </FestivalCompetitionBannerAddress>
          )}
          <div className="title">
            {competition?.title}
            <br />
            {competition?.subTitle}
          </div>
          <div className="date">{getDate()}</div>
        </FestivalCompetitionBannerText>
      </FestivalCompetitionImageBanner>
      <RabbitFestivalCompetitionScrollingWrapper
        ref={sponsorRef}
        duration={sponsorDuration}
        $isVisible={imagesLoaded}
      >
        <div className="scroll" ref={sponsorScrollRef}>
          {extendedSponsors.map((item, i) => (
            <img key={i} src={item} alt="scrolling" />
          ))}
        </div>
      </RabbitFestivalCompetitionScrollingWrapper>
    </FestivalCompetitionBannerWrapper>
  );
};

export default RabbitFestivalCompetitionBanner;
